import { cardLabel, SUIT_NAMES, SUIT_SYMBOLS, SUITS, isRed } from '@cardengine/shared';
import { useStore } from '../store.js';

/**
 * Shown to the player who just laid a suit changer and must nominate a suit.
 *
 * Which card that is depends on the house rules — Jacks by default, Aces under
 * Classic Switch — so nothing here names a rank. It reads the card off the top
 * of the pile instead, which is right whatever the table is playing.
 *
 * The panel covers the table, including the discard pile's suit badge, so it
 * has to carry that information itself: which suit is in force right now, and
 * therefore which option leaves it alone.
 */
export function SuitPicker() {
  const game = useStore((s) => s.game);
  const chooseSuit = useStore((s) => s.chooseSuit);

  if (!game || game.phase !== 'choosingSuit') return null;

  const mine = game.awaitingSuitFrom === game.yourPlayerId;
  const chooser = game.players.find((p) => p.id === game.awaitingSuitFrom);
  const current = game.activeSuit;

  /*
   * How many of each suit you are holding.
   *
   * This is the actual decision — you pick the suit you can follow — and the
   * panel covers your hand while you make it. Without this you are choosing
   * from memory, which is the one thing the interface should never ask for.
   *
   * Counted after the changer has left your hand, so these are the cards you
   * will still be holding when the choice takes effect.
   */
  const held = new Map(SUITS.map((suit) => [suit, 0]));
  for (const card of game.yourHand) held.set(card.suit, (held.get(card.suit) ?? 0) + 1);

  // The card is named twice over if the chooser is told both what they played
  // and what is on the pile — it is the same card.
  const inPlay = (withTopCard: boolean) => (
    <p className="modal__hint suit-current">
      In play now:{' '}
      <span className={`suit-current__suit ${isRed(current) ? 'is-red' : 'is-black'}`}>
        {SUIT_SYMBOLS[current]} {SUIT_NAMES[current]}
      </span>
      {withTopCard && game.topCard && (
        <span className="suit-current__top"> · top card {cardLabel(game.topCard)}</span>
      )}
    </p>
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Choose a suit">
      <div className="modal__panel">
        {mine ? (
          <>
            <h2 className="modal__title">Pick a suit</h2>
            <p className="modal__hint">
              Your {game.topCard ? cardLabel(game.topCard) : 'card'} changes the suit in play.
            </p>
            {inPlay(false)}
            <div className="suit-grid">
              {SUITS.map((suit) => (
                <button
                  key={suit}
                  type="button"
                  className={[
                    'suit-grid__option',
                    isRed(suit) ? 'is-red' : 'is-black',
                    suit === current ? 'is-current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => chooseSuit(suit)}
                  // Start on the suit already in play: it is the one option
                  // whose effect the player can predict without reading.
                  autoFocus={suit === current}
                  aria-label={[
                    SUIT_NAMES[suit],
                    `${held.get(suit) ?? 0} in your hand`,
                    suit === current ? 'the suit already in play' : '',
                  ]
                    .filter(Boolean)
                    .join(', ')}
                >
                  <span className="suit-grid__symbol">{SUIT_SYMBOLS[suit]}</span>
                  <span className="suit-grid__name">{SUIT_NAMES[suit]}</span>
                  <span className={`suit-grid__held ${held.get(suit) ? '' : 'is-none'}`}>
                    {held.get(suit) ? `${held.get(suit)} in hand` : 'none in hand'}
                  </span>
                  {suit === current && <span className="suit-grid__tag">current</span>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <h2 className="modal__title">{chooser?.name ?? 'A player'} is choosing a suit</h2>
            {inPlay(true)}
            <p className="modal__hint">Hold tight.</p>
          </>
        )}
      </div>
    </div>
  );
}
